import React, { useEffect, useRef, useState } from "react";
import BaseModel from "../models/BaseModel";
import Logger from "../Logger";
import { RDiv } from "./renderPrimitives/RDiv";
import { RenderTarget, useRenderTarget } from "./renderPrimitives/renderTarget";
import { RText } from "./renderPrimitives/RText";
import { showErrToast, showToast } from "./Toasts";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import type { PlacesType, VariantType } from "react-tooltip";

const logger = new Logger();

export default function EditableText(props: {
  className?: string;
  obj: BaseModel | undefined;
  property: string;
  propertyType?: "text" | "numeric";
  multiline?: boolean;
  style?: any;
  disableEdit?: boolean;
  onChange?: () => void;
  onCancel?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onEnterkey?: () => void;
  children?: any;
  upperCase?: boolean;
  placeholder?: string;
  maxCharacters?: number;
  maxWidth?: number;
  tabIndex?: number;
  checklistValueDouble?: () => Array<string> | undefined;
  maxValueNumeric?: number;
  emptyForbidden?: boolean;
  showBorder?: boolean;

  disableValidCheck?: boolean;
  isFloatNumber?: boolean;
  tooltipVariant?: VariantType;
  tooltipText?: string;
  tooltipPlace?: PlacesType;

  showToastX?: (params: any) => void;
}) {
  const {
    className,
    obj,
    property,
    propertyType,
    multiline,
    style,
    onChange,
    onFocus,
    onCancel,
    onEnterkey,
    upperCase,
    maxWidth,
    tabIndex,
    checklistValueDouble,
    disableValidCheck,
    maxValueNumeric,
    onBlur: onBlurrFunction,
    emptyForbidden,
    showBorder,
    showToastX,
    tooltipVariant,
    tooltipText,
    tooltipPlace,
  } = props;

  let { disableEdit } = props;

  const target = useRenderTarget();

  const mouseIsDown = useRef<boolean>(false);
  const activePointerId = useRef<number | null>(null);

  let { maxCharacters } = props;
  let { placeholder } = props;

  const autoFocus = false;

  const PLACEHOLDER = "__";
  const elmRef = useRef<any>(null);

  useEffect(() => {
    const preventNonTextPaste = (event: ClipboardEvent) => {
      if (!event.clipboardData?.types.includes("text/plain")) {
        event.preventDefault();
      }
    };

    window.addEventListener("paste", preventNonTextPaste);
    return () => window.removeEventListener("paste", preventNonTextPaste);
  }, []);

  const [isComposing, setIsComposing] = useState(false);
  const [focusMinWidthPx, setFocusMinWidthPx] = useState<number | undefined>(undefined);

  // DOM initial/extern syncen, ohne Caret zu zerstören
  useEffect(() => {
    const elm = elmRef.current;
    if (!elm) return;
    const domVal = getDomValue(elm);
    const value = `${(obj as any)[property]}`; // sometimes [property] is not a string

    const next = normalize(value);
    if (domVal !== next) {
      // setze *text* (kein HTML); CSS white-space: pre-wrap übernimmt \n
      elm.textContent = next;
      toggleEmptyAttr();
    }
  }, []);

  useEffect(() => {
    if (autoFocus && elmRef.current) {
      elmRef.current.focus();
      placeCaretAtEnd(elmRef.current);
    }
  }, [autoFocus]);

  useEffect(() => {
    const resetPointerState = () => {
      mouseIsDown.current = false;
      activePointerId.current = null;
    };
    window.addEventListener("pointerup", resetPointerState);
    window.addEventListener("pointercancel", resetPointerState);
    return () => {
      window.removeEventListener("pointerup", resetPointerState);
      window.removeEventListener("pointercancel", resetPointerState);
    };
  }, []);

  useOnChangeWatcher(obj, (changedProperty, changedObj) => {
    if (changedProperty === property || property === "displayName") {
      const elm = elmRef.current;
      if (elm) {
        const newValue = `${(obj as any)[property]}`;
        const domVal = getDomValue(elm);
        if (domVal !== newValue) {
          elm.textContent = newValue;
          toggleEmptyAttr();
        }
      }
    }
    return false;
  });

  function normalize(val: string) {
    // Normalisiere Zeilenumbrüche, NBSP etc.
    return val.replace(/\r\n?/g, "\n").replace(/\u00A0/g, " ");
  }

  function getDomValue(div: HTMLDivElement) {
    // innerText gibt dir „sichtbaren“ Plaintext inkl. \n
    return normalize(div.innerText);
  }

  function setCaret(elm: HTMLElement, pos: number) {
    try {
      var range = document.createRange();
      var sel = window.getSelection();

      range.setStart(elm.childNodes[0], pos);
      range.collapse(true);

      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch (ex) {
      logger.error(ex);
    }
  }

  // define the size of the edible Textview
  function keyUpEvent(ev: any) {
    if (!disableValidCheck) {
      if (maxCharacters || property == "name" || property == "displayName" || property == "tag") {
        if (!maxCharacters && (property == "name" || property == "displayName")) {
          maxCharacters = obj?.maxNameCharacters;
        } else if (!maxCharacters && property == "tag") {
          maxCharacters = obj?.maxTagCharacters;
        }
        if (maxCharacters && ev.target.innerText.length > maxCharacters && ev.keyCode != 8) {
          ev.target.innerText = ev.target.innerText.substring(0, maxCharacters);
          setCaret(ev.target, maxCharacters);
        }
        if (property == "tag") {
          // Fuse tags use "<elcbTag>.<localIndex>" (e.g. F1.1), so "." must stay allowed
          const allowedTagCharsRegex = obj?.className == "Fuse" ? /[^a-zA-Z0-9.,-]/g : /[^a-zA-Z0-9]/g; // no itx
          if (ev.target.innerText.replace(allowedTagCharsRegex, "") != ev.target.innerText) {
            ev.target.innerText = ev.target.innerText.replace(allowedTagCharsRegex, "");
            setCaret(ev.target, ev.target.innerText.length);
          }
        }
      }
    }
  }

  function onKeyDown(ev: any) {
    var key = ev.which || ev.keyCode; // Detecting keyCode

    // Detecting Ctrl
    var ctrl = ev.ctrlKey ? ev.ctrlKey : key === 17 ? true : false;
    ctrl = ctrl || ev.metaKey;

    if (ctrl && key == 86) {
      // if key V is pressed and ctrl is true
      //check if copied text or number
      logger.log("copied element", ev.target.value);
    }
    if (ev.keyCode == 27) {
      ev.target.innerText = (obj as any)[property];
      if (onCancel) {
        onCancel();
      }
      ev.target.blur();
    } else if (ev.keyCode == 13) {
      if (!multiline) {
        if (onEnterkey) {
          onBlur(ev);
          onEnterkey();
        }
        (window as any).getSelection()?.removeAllRanges();
        ev.stopPropagation();
        ev.preventDefault();
      }
    }
    maxCharacters;
    keyUpEvent(ev);
  }
  function onBlur(ev: any) {
    try {
      setFocusMinWidthPx(undefined);
      const lastValue = (obj as any)[property];
      const el = elmRef.current;

      if (!el) {
        return;
      }
      let value: any = getDomValue(el).trim();

      if (value == lastValue) {
        return;
      }

      let setValue = true;
      let showWarning: string = "";

      // Converts sequences like ^2, ^(, ^+ to their unicode superscript equivalents.
      if (propertyType !== "numeric") {
        const superscriptMap: Record<string, string> = {
          "0": "⁰", // no itx
          "1": "¹", // no itx
          "2": "²", // no itx
          "3": "³", // no itx
          "4": "⁴", // no itx
          "5": "⁵", // no itx
          "6": "⁶", // no itx
          "7": "⁷", // no itx
          "8": "⁸", // no itx
          "9": "⁹", // no itx
          "+": "⁺", // no itx
          "-": "⁻", // no itx
          "=": "⁼", // no itx
          "(": "⁽", // no itx
          ")": "⁾", // no itx
          n: "ⁿ", // no itx
          i: "ⁱ", // no itx
        };
        value = value.replace(/\^([0-9+\-=()ni])/g, (match: string, ch: string) => superscriptMap[ch] || match);
        ev.target.innerText = value;
      }

      ////////////////////////////
      // tag
      ///////////////////////////
      if (property == "tag" || property == "tagOrName") {
        // empty value as tag forbidden exept for cabinet
        const allCabinetsTags = obj?.getAllTagsFromSameTypeWithoutOwn() || [];

        if (value == "" && obj?.className != "Cabinet" && !allCabinetsTags.includes("")) {
          setValue = false;
          showWarning = "emptyTag"; // no itx
        }

        // check if double tag
        if (!disableValidCheck && obj && setValue) {
          // set value to max characters
          if (value.length > obj.maxTagCharacters) {
            value = value.substring(0, obj.maxTagCharacters);
          }
          // check if tag exists
          const allTagsWithoutOwn = obj.getAllTagsFromSameTypeWithoutOwn() || [];
          if (allTagsWithoutOwn.includes(value)) {
            showWarning = "doubleTag"; // no itx
            value = state.nameAndTagManager.getAvailableTagWithBracetsIfNeeded(value, allTagsWithoutOwn);
          }
          // check if din norm chars are in tag
          // Fuse tags allow "." and "-" for elcb-local numbering and multi-pole ranges (e.g. F1.1, F1.2-4)
          let valueWithoutForbiddenDinValues = value.replace("=", "").replace("+", "").replace("/", "");
          if (obj?.className != "Fuse") {
            valueWithoutForbiddenDinValues = valueWithoutForbiddenDinValues.replace("-", "").replace(".", "");
          }

          if (valueWithoutForbiddenDinValues != value) {
            value = valueWithoutForbiddenDinValues;
            showWarning = "forbiddenDinChars";
          }
        }
      } else {
        // check if value is in checklist
        if (checklistValueDouble && value != "") {
          const cl = checklistValueDouble();
          if (cl?.includes(value)) {
            setValue = false;
            showWarning = "double"; // no itx
          }
        }
        // numeric value
        if (setValue && propertyType == "numeric") {
          if (props.isFloatNumber) {
            value = value.replace(",", "."); // allow comma as decimal separator
            value = parseFloat(value);

            // Exceptional handling for physicalAddress
          } else if (!(property == "areaAddress" || property == "lineAddress" || property == "physicalAddress")) {
            value = parseInt(value);
          }
          // not in range
          if (maxValueNumeric && value > maxValueNumeric) {
            showWarning = "notInRange"; // no itx
            setValue = false; // no itx
          }
        }
      }
      if (value instanceof String && value.includes("/")) {
        value = value.replace("/", "_");
        showWarning = "forbiddenChars";
      }
      if (showWarning != "") {
        let text = showWarning;
        switch (showWarning) {
          case "emptyTag":
            text = state.translate("handleTagEdit.emptyForbidden", "leer nicht erlaubt");
            break;
          case "doubleTag":
            text = state.translate("handleTagEdit.T1AlreadyPresent", "eingegebener Text existiert schon", {
              T1: ev.target.innerText,
            });
            break;
          case "forbiddenDinChars":
            text = state.translate(
              "handleTagEdit.forbiddenDinChars",
              "Der Tag darf keine der folgenden Zeichen enthalten: = - + . /",
            );
            break;
          case "double":
            text = state.translate("handleTagEdit.double", "Der eingegebene Text existiert bereits.");
            break;
          case "notInRange":
            text = state.translate("handleTagEdit.numberToHigh", "die Zahl T1 ist nicht im zugewiesenen wertebereich");
            break;
          case "forbiddenChars":
            text = state.translate(
              "handleTagEdit.forbiddenChars",
              "Es darf keine der folgenden Zeichen enthalten sein: /",
            );
            break;
        }
        showToast({ text: text });
      }
      if (obj && setValue) {
        // to upper case
        if (upperCase || property == "tag" || property == "tagOrName") {
          value = value.toUpperCase();
        }
        // setValue
        (obj as any)[property] = value;
        const v1 = (obj as any)[property];
        if (ev.target.innerText != v1) {
          showToast({
            text: state.translate("adjustValue", "Der Wert wurde durch Estada angepasst(T1 -> T2).", {
              T1: ev.target.innerText,
              T2: v1,
            }),
          });
        }
        ev.target.innerText = v1;
        obj.raiseOnChange(property);
        if (onChange) {
          onChange();
        }
      }

      (window as any).getSelection()?.removeAllRanges();

      if (onBlurrFunction) {
        onBlurrFunction();
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  function toggleEmptyAttr() {
    const el = elmRef.current!;
    const empty = getDomValue(el).length === 0;
    el.setAttribute("data-empty", empty ? "true" : "false"); // no itx
  }

  function emitChange() {
    const el = elmRef.current!;
    const v = getDomValue(el);
    onChange && onChange();
    toggleEmptyAttr();
  }

  function handleInput() {
    if (isComposing) return; // während IME nichts beschneiden

    emitChange();
  }

  function handleBeforeInput(e: React.FormEvent<HTMLDivElement> & InputEvent) {
    // Verhindere Rich-Text-Formatierung (Bold, Italic, etc.)
    // sowie Einfügen, das maxLength überschreitet.
    const el = elmRef.current!;
    const type = e.inputType || "";
    if (type.startsWith("format") || type === "insertLink") {
      e.preventDefault();
      return;
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    // Immer als Plaintext einfügen
    e.preventDefault();
    const text = normalize(e.clipboardData.getData("text/plain"));
    if (!text) return;
    insertText(text);
    emitChange();
  }

  function insertText(text: string) {
    // Erst versuchsweise „insertText“, das \n verstanden wird
    const ok = document.execCommand && document.execCommand("insertText", false, text);
    if (ok) return;

    // Fallback: Range API
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.setEndAfter(node);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  function placeCaretAtEnd(el: HTMLElement) {
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  if (props.children && props.children > 0) {
    logger.error("EditableText may not have children");
  }

  disableEdit ||= target === RenderTarget.pdf;

  var txt = (obj as any)[property];
  let xplaceholder = placeholder || PLACEHOLDER;

  let style1: any = {};
  if (disableEdit) {
    style1.pointerEvents = "none";
    style1.cursor = "default";
    style1.userSelect = "none";
  }
  // define to uppercase or not while writing

  if (upperCase && !disableEdit) {
    style1.transformText = "uppercase"; // no itx
  } else {
    style1.transformText = "none";
  }

  if (maxWidth) {
    style1.maxWidth = `${maxWidth}px`;
  }

  if (style) {
    style1 = {
      ...style1,
      ...style,
    };
  }

  if (focusMinWidthPx !== undefined) {
    style1.minWidth = `${focusMinWidthPx}px`;
  }

  if ((!txt && !disableEdit) || showBorder) {
    if (style?.outline == undefined || !style?.outline) {
      style1.outline = "var(--editableTextBorder)"; // no itx
    }

    style1.minHeight = "25px";
  }

  if (showBorder == false) {
    style1.outline = "none";
  }

  if ((!txt && !disableEdit) || showBorder) {
    style1.minHeight = "25px";
  }

  // if ((!txt && !disableEdit) || showBorder) {
  //   style1.pointerEvents = "none";
  // }

  if (disableEdit) {
    return (
      <RDiv
        key="editableTextDisabled"
        data-noitx="true" // no itx
        ref={elmRef}
        suppressContentEditableWarning={true}
        style={style1}
        // onpaste="DetectChange();"
        className={"nameEdit disabled " + (className || "")} // no itx
      >
        <RText>{txt}</RText>
      </RDiv>
    );
  } else {
    return (
      <RDiv
        key="editableTextEnabled"
        data-noitx="true" // no itx
        ref={elmRef}
        contentEditable={!disableEdit}
        role="textbox" // no itx
        aria-multiline={multiline ? "true" : "false"} // no itx
        aria-disabled={disableEdit}
        aria-readonly={disableEdit}
        aria-label={placeholder}
        // spellCheck={spellCheck}
        data-empty={txt ? "false" : "true"} // no itx
        draggable="false" // no itx
        tabIndex={tabIndex}
        data-placeholder={xplaceholder} // made with style because div isn`t supporting placeholder
        suppressContentEditableWarning={true}
        style={style1}
        // onpaste="DetectChange();"
        className={"nameEdit " + (className || "")} // no itx
        onKeyDown={(ev) => onKeyDown(ev)}
        onKeyUp={(ev) => keyUpEvent(ev)}
        onMouseDown={(ev) => {
          mouseIsDown.current = true;
          ev.stopPropagation();
        }}
        onMouseUp={(ev) => {
          mouseIsDown.current = false;
          ev.stopPropagation();
        }}
        onPointerDown={(ev) => {
          mouseIsDown.current = true;
          activePointerId.current = ev.pointerId;
          try {
            (ev.currentTarget as HTMLDivElement).setPointerCapture(ev.pointerId);
          } catch {
            // ignore browsers/devices where pointer capture is unsupported
          }
          ev.stopPropagation();
        }}
        onPointerUp={(ev) => {
          mouseIsDown.current = false;
          if (activePointerId.current === ev.pointerId) {
            activePointerId.current = null;
          }
          try {
            (ev.currentTarget as HTMLDivElement).releasePointerCapture(ev.pointerId);
          } catch {
            // ignore
          }
          ev.stopPropagation();
        }}
        onPointerCancel={(ev) => {
          mouseIsDown.current = false;
          if (activePointerId.current === ev.pointerId) {
            activePointerId.current = null;
          }
          try {
            (ev.currentTarget as HTMLDivElement).releasePointerCapture(ev.pointerId);
          } catch {
            // ignore
          }
          ev.stopPropagation();
        }}
        onClick={(ev) => {
          ev.stopPropagation();
        }}
        onDoubleClick={(ev) => {
          ev.stopPropagation();
        }}
        onBlur={(ev) => {
          onBlur(ev);
        }}
        onFocus={(ev) => {
          const el = elmRef.current as HTMLDivElement | null;
          if (el) {
            const currentWidth = Math.ceil(el.getBoundingClientRect().width);
            if (Number.isFinite(currentWidth) && currentWidth > 0) {
              setFocusMinWidthPx(currentWidth);
            }
          }
          if (onFocus) {
            onFocus();
          }
        }}
        onInput={handleInput}
        onBeforeInput={handleBeforeInput as any}
        onPaste={handlePaste}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => {
          setIsComposing(false);
          handleInput();
        }}
        // onClick={() => setEdit(true)}
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={tooltipText}
        data-tooltip-place={tooltipPlace}
        data-tooltip-variant={tooltipVariant}
      >
        {txt}
      </RDiv>
    );
  }
}
